import React, { useCallback, useRef, useState } from 'react';
import { ProgressBar } from 'react-bootstrap';
import { drapcodeIcon } from '../custom/icon-library';
import voca from 'voca';
import { toast } from 'react-toastify';
import { useDropzone } from 'react-dropzone';
import axios, { isCancel } from 'axios';
import { builderSurfaceMultiPartHeader } from '../utils/restApiHeaderUtils';

export const FileUpload = ({
  height = '200px',
  width = '100%',
  projectId,
  maxFiles,
  maxSizeBytes,
  url,
  accept,
  fileIcon,
  submitAction,
  uploadFile,
  defaultUpload = true,
  instructions = [],
  disabled = false,
}) => {
  const [files, setFiles] = useState([]);
  const [percent, setPercent] = useState(0);
  const [status, setStatus] = useState('');
  const cancelController = useRef(null);

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (disabled) return;
      console.log('acceptedFiles FileUpload', acceptedFiles);
      console.log('files FileUpload', files);
      const newFiles = [...files, ...acceptedFiles];
      setFiles(newFiles);
      const body = new FormData();
      body.append('file', acceptedFiles[0]);
      cancelController.current = axios.CancelToken.source();
      const header = builderSurfaceMultiPartHeader(projectId);
      const config = {
        headers: header.headers,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setPercent(percentCompleted);
        },
        cancelToken: cancelController.current.token,
      };
      setStatus('uploading');
      if (defaultUpload === false) {
        uploadFile(acceptedFiles[0]);
        setStatus('canceled');
        setPercent(100);
      } else {
        axios
          .post(url, body, config)
          .then((res) => {
            setStatus('done');
            toast.success('File Upload Successfully !', {});
          })
          .catch((err) => {
            if (isCancel(err)) {
              setStatus('canceled');
              toast.success('Request Canceled !');
            } else {
              setStatus('error');
              if (
                err.response &&
                err.response.status === 409 &&
                err.response.data &&
                err.response.data.message
              ) {
                toast.error(`Error: ${err.response.data.message}`);
              } else {
                toast.error(`Error: ${err.message}`);
              }
            }
          });
      }
    },
    [disabled, files, projectId, defaultUpload, uploadFile, url],
  );

  const { getRootProps, getInputProps, isDragAccept, isDragReject, open } = useDropzone({
    onDrop,
    accept,
    maxSize: maxSizeBytes,
    maxFiles,
    disabled,
  });

  const borderColor = isDragAccept
    ? 'border-success'
    : isDragReject
    ? 'border-danger'
    : 'border-dark';

  const buttonText = () => {
    switch (status) {
      case '':
        return 'Add File';
      case 'uploading':
        return 'Cancel';
      case 'error':
      case 'canceled':
        return 'Clear';
      default:
        return voca.titleCase(status);
    }
  };

  const handleSubmit = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }

    if (defaultUpload === false) {
      setFiles([]);
      setStatus('');
      setPercent(0);
    } else if (!status) {
      e.stopPropagation();
      open();
    } else if (status === 'uploading') {
      if (cancelController.current) {
        cancelController.current.cancel();
      }
    } else if (['canceled', 'error', 'done'].includes(status)) {
      setFiles([]);
      setStatus('');
      setPercent(0);
      if (status === 'done' && submitAction) {
        submitAction();
      }
    }
  };

  const Preview = () => {
    return (
      <div className="w-100 h-100 d-flex flex-column justify-content-center align-items-center">
        {files.map((file, i) => (
          <React.Fragment key={i}>
            <div className="py-2">
              <i className={`wb ${drapcodeIcon(fileIcon)}`} />
              &nbsp;
              <b>{file.name}</b>
            </div>
            <ProgressBar
              className="w-75"
              variant="success"
              now={percent}
              animated={!(status === 'done')}
            />
          </React.Fragment>
        ))}
      </div>
    );
  };

  const Input = (getInputProps) => {
    return (
      <>
        <input {...getInputProps()} />
        <div className="d-flex flex-column justify-content-center align-items-center">
          <i className="webconnect-cloud-arrow-up-fill" />
          <b>Drag Files or Click to Browse</b>
          {accept && <p className="pt-2">Only {voca.titleCase(accept)} files are Supported</p>}
          {instructions.map((info, index) => (
            <i key={index}>{info}</i>
          ))}
        </div>
      </>
    );
  };

  return (
    <div
      style={{
        height,
        width,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
      className={`d-flex justify-content-center flex-column align-items-center border rounded ${borderColor}`}
      {...getRootProps()}
    >
      {files.length === 0 ? Input(getInputProps) : Preview()}
      <button
        type="button"
        className="btn btn-theme-submit"
        onClick={disabled ? null : handleSubmit}
        disabled={disabled}
      >
        {buttonText()}
      </button>
    </div>
  );
};
